import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ClipboardList, UserCheck, Eye, EyeOff, Save, GripVertical, CalendarDays, MapPin, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LineupPanel({ orgId, teams, userRole }) {
  const { isDark } = useTheme();
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [upcomingLineups, setUpcomingLineups] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingGameId, setEditingGameId] = useState(null);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const [lineupPlayers, setLineupPlayers] = useState([]);
  const [benchPlayers, setBenchPlayers] = useState([]);
  const [formation, setFormation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const canEdit = ['company_support', 'coach'].includes(userRole);

  useEffect(() => {
    if (teams?.length > 0 && !selectedTeamId) setSelectedTeamId(teams[0]?.id);
  }, [teams]);

  useEffect(() => {
    if (selectedTeamId) { fetchLineups(); fetchGames(); }
  }, [selectedTeamId]);

  const fetchLineups = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lineup/team/${selectedTeamId}/upcoming`, { withCredentials: true });
      setUpcomingLineups(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchGames = async () => {
    if (!orgId) return;
    try {
      const res = await axios.get(`${API_URL}/api/availability/${orgId}/events`, { withCredentials: true });
      // Only show actual games for lineup creation (not practices/meetings)
      const gameOnly = (res.data || []).filter(e => e.event_category === 'game' || !e.event_category);
      setGames(gameOnly);
    } catch (err) { console.error(err); }
  };

  const openLineupEditor = async (gameId) => {
    setEditingGameId(gameId);
    // Load roster with availability
    try {
      const rosterRes = await axios.get(`${API_URL}/api/lineup/team/${selectedTeamId}/roster-for-game?game_id=${gameId}`, { withCredentials: true });
      setRosterPlayers(rosterRes.data || []);
    } catch (err) { setRosterPlayers([]); }
    // Load existing lineup
    try {
      const lineupRes = await axios.get(`${API_URL}/api/lineup/game/${gameId}`, { withCredentials: true });
      setLineupPlayers(lineupRes.data?.players || []);
      setBenchPlayers(lineupRes.data?.bench || []);
      setFormation(lineupRes.data?.formation || '');
      setNotes(lineupRes.data?.notes || '');
    } catch (err) {
      setLineupPlayers([]);
      setBenchPlayers([]);
      setFormation('');
      setNotes('');
    }
    setShowEditor(true);
  };

  const addToLineup = (player) => {
    if (lineupPlayers.find(p => p.player_id === player.id)) return;
    setLineupPlayers([...lineupPlayers, {
      player_id: player.id,
      name: `${player.first_name} ${player.last_name}`,
      jersey: player.jersey_number || '',
      position: player.position || '',
      order: lineupPlayers.length + 1
    }]);
  };

  const addToBench = (player) => {
    if (benchPlayers.find(p => p.player_id === player.id) || lineupPlayers.find(p => p.player_id === player.id)) return;
    setBenchPlayers([...benchPlayers, {
      player_id: player.id,
      name: `${player.first_name} ${player.last_name}`,
      jersey: player.jersey_number || ''
    }]);
  };

  const removeFromLineup = (playerId) => {
    setLineupPlayers(lineupPlayers.filter(p => p.player_id !== playerId));
  };

  const removeFromBench = (playerId) => {
    setBenchPlayers(benchPlayers.filter(p => p.player_id !== playerId));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const arr = [...lineupPlayers];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    arr.forEach((p, i) => p.order = i + 1);
    setLineupPlayers(arr);
  };

  const moveDown = (idx) => {
    if (idx >= lineupPlayers.length - 1) return;
    const arr = [...lineupPlayers];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    arr.forEach((p, i) => p.order = i + 1);
    setLineupPlayers(arr);
  };

  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    const arr = [...lineupPlayers];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(dropIdx, 0, moved);
    arr.forEach((p, i) => p.order = i + 1);
    setLineupPlayers(arr);
    setDragIdx(null);
  };

  const saveLineup = async (publish = false) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/lineup/game/${editingGameId}`, {
        team_id: selectedTeamId, org_id: orgId, players: lineupPlayers,
        bench: benchPlayers, formation, notes, published: publish
      }, { withCredentials: true });
      if (publish) {
        await axios.put(`${API_URL}/api/lineup/game/${editingGameId}/publish`, {}, { withCredentials: true });
      }
      setShowEditor(false);
      fetchLineups();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const inLineupIds = new Set([...lineupPlayers.map(p => p.player_id), ...benchPlayers.map(p => p.player_id)]);

  return (
    <div className="space-y-4" data-testid="lineup-panel">
      {teams && teams.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {teams.map(t => (
            <Button key={t.id} size="sm" variant={selectedTeamId === t.id ? 'default' : 'outline'}
              onClick={() => setSelectedTeamId(t.id)}
              className={selectedTeamId === t.id ? 'bg-amber-600 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}
              data-testid={`lineup-team-${t.id}`}>{t.name}</Button>
          ))}
        </div>
      )}

      {/* Existing Lineups */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <ClipboardList className="h-5 w-5 text-orange-500" /> Game Day Lineups
              <Badge variant="outline" className="ml-2 text-xs">{upcomingLineups.length}</Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading...</p> :
          upcomingLineups.length > 0 ? (
            <div className="space-y-2">
              {upcomingLineups.map(lineup => (
                <div key={lineup.id || lineup.game_id} className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-slate-200 bg-slate-50'}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-slate-400" />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {lineup.game_info?.date || 'Unknown'} vs {lineup.game_info?.opponent || 'TBD'}
                      </span>
                    </div>
                    <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {lineup.players?.length || 0} starters, {lineup.bench?.length || 0} bench
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {lineup.published ? (
                      <Badge className="bg-green-500/10 text-green-600"><Eye className="h-3 w-3 mr-1" /> Published</Badge>
                    ) : (
                      <Badge className="bg-yellow-500/10 text-yellow-600"><EyeOff className="h-3 w-3 mr-1" /> Draft</Badge>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => openLineupEditor(lineup.game_id)}
                        className={isDark ? 'border-slate-600 text-slate-300' : ''}>Edit</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Available Games for Lineup */}
          {canEdit && games.length > 0 && (
            <div className="mt-4">
              <p className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Create lineup for upcoming game:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {games.slice(0, 6).map(game => {
                  const hasLineup = upcomingLineups.some(l => l.game_id === game.id);
                  return (
                    <Button key={game.id} variant="outline" size="sm"
                      className={`justify-start ${isDark ? 'border-slate-600 text-slate-300' : ''} ${hasLineup ? 'opacity-50' : ''}`}
                      onClick={() => !hasLineup && openLineupEditor(game.id)} disabled={hasLineup}
                      data-testid={`lineup-game-${game.id}`}>
                      <Plus className="h-3 w-3 mr-2" />
                      {game.date} vs {game.opponent || 'TBD'} {hasLineup && '(done)'}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && upcomingLineups.length === 0 && games.length === 0 && (
            <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No games scheduled for lineups</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* LINEUP EDITOR DIALOG */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className={`max-w-3xl max-h-[80vh] overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Edit Lineup</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {/* Available Roster */}
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Available Roster</Label>
              <div className={`border rounded p-2 max-h-60 overflow-y-auto space-y-1 mt-1 ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                {rosterPlayers.filter(p => !inLineupIds.has(p.id)).map(p => (
                  <div key={p.id} className={`flex items-center justify-between p-1.5 rounded text-sm ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-center gap-1.5">
                      {p.availability && (
                        <span className={`w-2 h-2 rounded-full shrink-0 ${
                          p.availability === 'yes' ? 'bg-green-500' :
                          p.availability === 'maybe' ? 'bg-yellow-500' :
                          p.availability === 'no' ? 'bg-red-500' : 'bg-gray-400'
                        }`} title={p.availability} />
                      )}
                      <span className={p.availability === 'no' ? 'line-through opacity-50' : ''}>{p.jersey_number && `#${p.jersey_number} `}{p.first_name} {p.last_name}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-green-600" onClick={() => addToLineup(p)}>Starter</Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-600" onClick={() => addToBench(p)}>Bench</Button>
                    </div>
                  </div>
                ))}
                {rosterPlayers.filter(p => !inLineupIds.has(p.id)).length === 0 && (
                  <p className={`text-xs text-center py-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>All players assigned</p>
                )}
              </div>
            </div>

            {/* Lineup */}
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Starting Lineup ({lineupPlayers.length})</Label>
              <div className={`border rounded p-2 max-h-40 overflow-y-auto space-y-1 mt-1 ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                {lineupPlayers.map((p, idx) => (
                  <div key={p.player_id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(idx)}
                    className={`flex items-center justify-between p-1.5 rounded text-sm cursor-grab active:cursor-grabbing ${dragIdx === idx ? 'opacity-50' : ''} ${isDark ? 'bg-slate-700/50' : 'bg-green-50'}`}>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-mono text-slate-400 w-4">{idx + 1}</span>
                      <div className="flex gap-0.5">
                        <button onClick={() => moveUp(idx)} className="text-xs text-slate-400 hover:text-white">&#9650;</button>
                        <button onClick={() => moveDown(idx)} className="text-xs text-slate-400 hover:text-white">&#9660;</button>
                      </div>
                      <span>{p.jersey && `#${p.jersey} `}{p.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Input value={p.position || ''} placeholder="Pos"
                        onChange={e => {
                          const arr = [...lineupPlayers];
                          arr[idx].position = e.target.value;
                          setLineupPlayers(arr);
                        }}
                        className={`h-6 w-16 text-xs ${isDark ? 'bg-slate-600 border-slate-500 text-white' : ''}`} />
                      <button onClick={() => removeFromLineup(p.player_id)} className="text-red-400 hover:text-red-500">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <Label className={`mt-3 ${isDark ? 'text-slate-300' : ''}`}>Bench ({benchPlayers.length})</Label>
              <div className={`border rounded p-2 max-h-24 overflow-y-auto space-y-1 mt-1 ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                {benchPlayers.map(p => (
                  <div key={p.player_id} className={`flex items-center justify-between p-1 rounded text-sm ${isDark ? 'bg-slate-700/30' : 'bg-blue-50'}`}>
                    <span>{p.jersey && `#${p.jersey} `}{p.name}</span>
                    <button onClick={() => removeFromBench(p.player_id)} className="text-red-400"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Formation</Label>
              <Input value={formation} onChange={e => setFormation(e.target.value)} placeholder="e.g., 4-4-2"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Coach Notes</Label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Game plan notes..."
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={() => saveLineup(false)} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="save-lineup-draft">
              <Save className="h-4 w-4 mr-1" /> Save Draft
            </Button>
            <Button onClick={() => saveLineup(true)} disabled={saving} className="bg-green-600 text-white hover:bg-green-700" data-testid="publish-lineup">
              <Eye className="h-4 w-4 mr-1" /> Save & Publish
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
